"use client"
import { Component } from "react"

class AutoPrintKOT extends Component {
  constructor(props) {
    super(props)
    this.state = {
      auto_kot_print: true,
      printQueue: [],
      isProcessingPrint: false,
    }
  }

  // Main auto-print method
  autoPrintKOT = async (kotData) => {
    try {
      console.log("🖨️ Auto printing KOT:", kotData.id)
      
      // Check if auto-print is enabled
      if (!this.state.auto_kot_print) {
        console.log("🔕 Auto-print is disabled")
        return false
      }

      // Check if KOT has items
      if (!kotData || !kotData.kot_items || kotData.kot_items.length === 0) {
        console.warn("⚠️ No KOT items to print")
        return false
      }

      // Try to print
      const printSuccess = await this.printKOT(kotData)
      
      if (printSuccess) {
        console.log(`✅ Print successful for KOT #${kotData.id}`)
        if (this.props.showToast) {
          this.props.showToast(`KOT #${kotData.id} printed successfully`, "success")
        }
        return true
      } else {
        console.warn(`⚠️ Print failed for KOT #${kotData.id}`)
        this.addToPrintQueue(kotData)
        if (this.props.showToast) {
          this.props.showToast(`Print failed for KOT #${kotData.id}`, "error")
        }
        return false
      }
    } catch (error) {
      console.error("❌ Auto-print error:", error)
      this.addToPrintQueue(kotData)
      return false
    }
  }

  // Print directly to printer without preview
  printKOT = (kotData) => {
    return new Promise((resolve) => {
      try {
        // Create a hidden iframe for printing
        const iframe = document.createElement('iframe')
        iframe.style.position = 'absolute'
        iframe.style.width = '1px'
        iframe.style.height = '1px'
        iframe.style.left = '-9999px'
        iframe.style.top = '-9999px'
        iframe.style.border = 'none'
        
        document.body.appendChild(iframe)
        
        // Generate the KOT HTML
        const printHTML = this.generateKOTHTML(kotData)
        
        // Write content to iframe
        iframe.contentDocument.write(printHTML)
        iframe.contentDocument.close()
        
        // Wait for content to load
        iframe.onload = () => {
          try {
            // Focus and print
            iframe.contentWindow.focus()
            iframe.contentWindow.print()
            
            // Remove iframe after printing
            setTimeout(() => {
              document.body.removeChild(iframe)
              resolve(true)
            }, 1000)
          } catch (printError) {
            console.error("❌ Print error:", printError)
            document.body.removeChild(iframe)
            resolve(false)
          }
        }
      } catch (error) {
        console.error("❌ Print error:", error)
        resolve(false)
      }
    })
  }

  // Generate thermal printer friendly HTML (same as your original)
  generateKOTHTML = (kotData) => {
    const currentTime = new Date().toLocaleString()

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>KOT #${kotData.id}</title>
          <style>
            @media print {
              @page { size: 80mm auto; margin: 2mm; }
              body { margin: 0; padding: 0; width: 80mm; }
            }
            body {
              margin: 0;
              padding: 10px;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              width: 80mm;
              background: white;
              color: black;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid black;
              padding-bottom: 8px;
              margin-bottom: 10px;
            }
            .kot-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .order-info {
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
              font-weight: bold;
            }
            .items-section {
              margin: 10px 0;
              border-top: 2px dashed black;
              padding-top: 10px;
            }
            .item-row {
              display: flex;
              margin: 8px 0;
              padding: 5px 0;
              border-bottom: 1px dotted #ccc;
            }
            .item-qty {
              font-weight: bold;
              width: 30px;
            }
            .item-name {
              flex: 1;
              margin-left: 10px;
              font-weight: bold;
            }
            .special-details {
              font-size: 10px;
              color: #666;
              margin-left: 40px;
              margin-top: 2px;
            }
            .footer {
              text-align: center;
              margin-top: 15px;
              border-top: 2px solid black;
              padding-top: 8px;
              font-size: 10px;
            }
            .table-info {
              text-align: center;
              font-weight: bold;
              margin: 5px 0;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="kot-title">KOT #${kotData.id}</div>
            <div>ORDER #${kotData.hotel_order_id}</div>
          </div>
          
          <div class="order-info">
            <span>TIME: ${currentTime}</span>
          </div>
          
          ${kotData.hotel_order?.order_type === "dinein" ? `
            <div class="table-info">
              TABLE: ${kotData.joinedFirst3Tables || "N/A"}
            </div>
          ` : ""}
          
          ${kotData.hotel_order?.order_type === "takeaway" ? `
            <div class="table-info">
              TAKEAWAY ORDER
            </div>
          ` : ""}
          
          <div class="items-section">
            ${kotData.kot_items.map(item => {
              let specialHTML = ""
              
              // Handle special item data
              if (item.special_data && item.special_data !== "null") {
                try {
                  const specialData = typeof item.special_data === "string" 
                    ? JSON.parse(item.special_data) 
                    : item.special_data

                  if (specialData && typeof specialData === "object") {
                    // Variations
                    if (specialData.variation_data) {
                      Object.entries(specialData.variation_data).forEach(([key, value]) => {
                        specialHTML += `<div class="special-details">${key}: ${value}</div>`
                      })
                    }

                    // Add-ons
                    if (specialData.add_ons_data && Object.keys(specialData.add_ons_data).length > 0) {
                      const addons = Object.keys(specialData.add_ons_data).join(", ")
                      specialHTML += `<div class="special-details">Add-ons: ${addons}</div>`
                    }

                    // Special request
                    if (specialData.special_request) {
                      specialHTML += `<div class="special-details">Note: ${specialData.special_request}</div>`
                    }
                  }
                } catch (e) {
                  console.error("Error parsing special data:", e)
                }
              }

              return `
                <div class="item-row">
                  <div class="item-qty">${item.item_quantity}x</div>
                  <div class="item-name">${item.item_menu_name}</div>
                </div>
                ${specialHTML}
              `
            }).join("")}
          </div>
          
          ${kotData.note ? `
            <div style="margin: 10px 0; padding: 5px; border: 1px dashed black;">
              <strong>NOTE:</strong> ${kotData.note}
            </div>
          ` : ""}
          
          <div class="footer">
            Printed: ${currentTime}
          </div>
        </body>
      </html>
    `
  }

  // Add failed prints to queue
  addToPrintQueue = (kotData) => {
    this.setState(prevState => ({
      printQueue: [...prevState.printQueue, {
        ...kotData,
        queuedAt: new Date().toISOString()
      }]
    }))
  }

  // Process print queue manually
  processPrintQueue = async () => {
    if (this.state.isProcessingPrint || this.state.printQueue.length === 0) {
      return
    }

    this.setState({ isProcessingPrint: true })

    for (const kotData of this.state.printQueue) {
      try {
        const success = await this.printKOT(kotData)
        if (success) {
          // Remove from queue
          this.setState(prevState => ({
            printQueue: prevState.printQueue.filter(item => item.id !== kotData.id)
          }))
        }
      } catch (error) {
        console.error("Error processing print queue:", error)
      }
    }

    this.setState({ isProcessingPrint: false })
  }

  // Enable/disable auto print
  toggleAutoPrint = () => {
    this.setState(prevState => ({
      auto_kot_print: !prevState.auto_kot_print
    }))
  }

  render() {
    const { printQueue, isProcessingPrint, auto_kot_print } = this.state

    return (
      <div>
        {/* Print Queue Indicator */}
        {printQueue.length > 0 && (
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              background: "#BB0A21",
              color: "white",
              padding: "10px 15px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              zIndex: 9999,
              cursor: "pointer",
            }}
            onClick={this.processPrintQueue}
          >
            🖨️ {printQueue.length} KOT{printQueue.length > 1 ? "s" : ""} in print queue
            {isProcessingPrint && <div style={{ fontSize: "12px" }}>Processing...</div>}
          </div>
        )}
{/* 
        <div
          style={{
            position: "fixed",
            bottom: "80px",
            right: "20px",
            background: auto_kot_print ? "#00A651" : "#666",
            color: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "12px",
            zIndex: 9998,
            cursor: "pointer",
          }}
          onClick={this.toggleAutoPrint}
          title="Click to toggle auto-print"
        >
          🖨️ Auto-Print: {auto_kot_print ? "ON" : "OFF"}
        </div> */}
      </div>
    )
  }
}

export default AutoPrintKOT